// src/pages/wallet/PharmaciesWithWalletPage.js

import { Grid, Paper, Button, Typography } from "@material-ui/core";
import PageTitle from "components/PageTitle/PageTitle";
import { useState, useCallback, useMemo } from "react";
import { SnackbarHandler } from "components/Handlers/SnackbarHandler";
import CustomizedSnackbars from "components/CustomizedSnackbars/CustomizedSnackbars";

import useStyles from "./styles";
import PharmacySelector from "components/CustomFields/PharmacySelector";
import PharmaciesWithWalletMuiDataTable from "components/MUIDatatable/PharmaciesWithWalletTable";
import DateField from "components/CustomFields/DateField";
import { getCurrentDate } from "utils/dateGetters.js";
import DistributorsField from "components/CustomFields/DistributorsField";
import RefreshIcon from "@material-ui/icons/Refresh";
import DoneIcon from "@material-ui/icons/Done";
import WarningIcon from "@material-ui/icons/Warning";

export default function PharmaciesWithWalletPage() {
  const classes = useStyles();

  // Filtro actual en edición
  const [selectedRut, setSelectedRut] = useState("");
  const [period, setPeriod] = useState(null);
  const [selectedDistributor, setSelectedDistributor] = useState("");
  // Filtro aplicado (lo que ve la grilla)
  const [applyClicked, setApplyClicked] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  // Sentinelas para forzar estado "dirty" al inicio
  const INIT = "__INIT__";
  const [lastApplied, setLastApplied] = useState({
    rut: INIT,
    period: INIT,          // YYYY-MM cuando se aplique por primera vez
    distributor: INIT,
  });


  const [snackbarStatus, snackbarOpen, onCloseSnackbar] = SnackbarHandler();


  // "YYYY-MM" desde Date o string
  const normPeriodStr = (d) =>
    !d ? null : (typeof d === "string" ? d.slice(0, 7) : new Date(d).toISOString().slice(0, 7));

  // "AAAAMM" (int) desde Date|string|null
  const toAaaammInt = (d) => {
    const yyyymm = normPeriodStr(d); // "YYYY-MM" | null
    if (!yyyymm) return null;
    const [y, m] = yyyymm.split("-");
    return Number(`${y}${m}`);
  };

  const normPeriod = (d) =>
    !d ? null : (typeof d === "string" ? d.slice(0, 7) : new Date(d).toISOString().slice(0, 7));

  const isDirty = useMemo(() => {
    return (
      (selectedRut || null) !== (lastApplied.rut || null) ||
      (String(selectedDistributor || "") || null) !== (String(lastApplied.distributor || "") || null) ||
      (normPeriodStr(period) || null) !== (lastApplied.period || null)
    );
  }, [selectedRut, selectedDistributor, period, lastApplied]);

  const onApply = () => {
    setApplyClicked(true);
    setReloadToken(t => t + 1);   // fuerza remount del grid

    setLastApplied({
      rut: selectedRut || null,
      distributor: selectedDistributor || null,       // string (lo convertimos al pasar al grid)
      period: normPeriodStr(period) || null,          // "YYYY-MM"
    });

  };

  const handleRutChange = (rut) => {
    setSelectedRut(rut || "");
  };

  const handlePeriodChange = (date) => {
    setPeriod(date);
  };

  const onChangeDistributor = (e) => {
    setSelectedDistributor(e.target.value || "");
  };


  return (
    <Grid>
      <CustomizedSnackbars
        severity={snackbarStatus.severity}
        mssg={snackbarStatus.message}
        open={snackbarStatus.open}
        onClose={onCloseSnackbar}
      />

      <PageTitle title="Billetera virtual - Farmacias con Billetera" />

      <Paper elevation={3} className={classes.paperContainer}>
        <Grid container direction="column" style={{ padding: 16, background: "#f5f5f5" }}>

          {/* Distribuidor + botón Aplicar */}
          <Grid
            container
            className={classes.filterRow}
            alignItems="center"
            justifyContent="space-between"
          >
            {/* Izquierda: label + selector (flexible) */}
            <Grid
              item
              xs
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,            // separa label y campo
                maxWidth: 500,        // importante para que el select no se colapse
              }}
            >
              <Typography style={{ flexShrink: 0 }}>Distribuidor</Typography>

              <div style={{ flexGrow: 1 }}>
                <DistributorsField
                  fieldClassName={`${classes.generalSelectionField} ${classes.fieldFullWidth}`}
                  value={selectedDistributor}
                  program={1}
                  onChange={onChangeDistributor}
                  only_with_wallet={true}
                  clearable
                  clearText="— Quitar distribuidor —"
                />
              </div>
            </Grid>

            {/* Derecha: botón Aplicar */}
            <Grid item>
              <Button
                variant="contained"
                size="small"
                onClick={onApply}
                startIcon={isDirty ? <RefreshIcon /> : <DoneIcon />}
                disabled={!isDirty}
                color={isDirty ? "secondary" : "default"}
                style={{ height: 40, minWidth: 120 }}
              >
                Aplicar
              </Button>
            </Grid>
          </Grid>

          {/* Grilla */}
          <Grid item xs={12} style={{ marginTop: 16, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <PharmaciesWithWalletMuiDataTable
              key={reloadToken}
              distributorId={
                lastApplied.distributor && lastApplied.distributor !== "__INIT__"
                  ? Number(lastApplied.distributor)
                  : null
              }
            />
          </Grid>
        </Grid>
      </Paper>

    </Grid>
  );
}
